import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  FaArrowLeft, FaPlus, FaEdit, FaTrash, FaKey, FaToggleOn, FaToggleOff, FaUniversity,
} from 'react-icons/fa';
import conciliacionBancariaService from '../../services/conciliacionBancariaService';
import '../../styles/pages/ConciliacionBancaria.css';

const TIPOS_CUENTA = [
  { value: 'corriente', label: 'Cuenta Corriente' },
  { value: 'vista', label: 'Cuenta Vista / RUT' },
  { value: 'ahorro', label: 'Cuenta de Ahorro' },
  { value: 'empresa', label: 'Cuenta Empresa' },
];

const FORM_INICIAL = {
  banco: '', nombre_cuenta: '', numero_cuenta: '', tipo_cuenta: 'corriente', moneda: 'CLP',
  integracion_tipo: '', integracion_usuario: '', integracion_password: '', integracion_api_key: '',
};

const CuentasBancoPage = ({ token, id_company }) => {
  const navigate = useNavigate();
  const [cuentas, setCuentas] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(FORM_INICIAL);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      const [cuentasData, bancosData] = await Promise.all([
        conciliacionBancariaService.getCuentas(id_company, token),
        conciliacionBancariaService.getBancos(token),
      ]);
      setCuentas(cuentasData);
      setBancos(bancosData);
    } catch {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudieron cargar las cuentas bancarias' });
    } finally {
      setLoading(false);
    }
  }, [id_company, token]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData(FORM_INICIAL);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.banco) {
      Swal.fire({ icon: 'warning', title: 'Atención', text: 'Selecciona un banco' });
      return;
    }
    try {
      const payload = { ...formData, id_company };
      if (editingId) {
        await conciliacionBancariaService.actualizarCuenta(editingId, payload, token);
        Swal.fire({ icon: 'success', title: 'Actualizado', text: 'Cuenta bancaria actualizada', timer: 1500, showConfirmButton: false });
      } else {
        await conciliacionBancariaService.crearCuenta(payload, token);
        Swal.fire({ icon: 'success', title: 'Creado', text: 'Cuenta bancaria agregada', timer: 1500, showConfirmButton: false });
      }
      resetForm();
      cargarDatos();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.error || 'Error al guardar' });
    }
  };

  const handleEdit = (cuenta) => {
    setFormData({
      banco: cuenta.banco,
      nombre_cuenta: cuenta.nombre_cuenta || '',
      numero_cuenta: cuenta.numero_cuenta || '',
      tipo_cuenta: cuenta.tipo_cuenta || 'corriente',
      moneda: cuenta.moneda || 'CLP',
      integracion_tipo: cuenta.integracion_tipo || '',
      integracion_usuario: cuenta.integracion_usuario || '',
      integracion_password: '',
      integracion_api_key: '',
    });
    setEditingId(cuenta.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id, nombre) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: '¿Eliminar cuenta?',
      html: `Se eliminarán también todos los movimientos importados de <b>${nombre}</b>.<br/>Esta acción no se puede deshacer.`,
      showCancelButton: true,
      confirmButtonColor: '#e53e3e',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });
    if (!result.isConfirmed) return;

    try {
      await conciliacionBancariaService.eliminarCuenta(id, token);
      Swal.fire({ icon: 'success', title: 'Eliminado', timer: 1200, showConfirmButton: false });
      cargarDatos();
    } catch {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo eliminar la cuenta' });
    }
  };

  const handleToggle = async (cuenta) => {
    try {
      await conciliacionBancariaService.actualizarCuenta(cuenta.id, {
        banco: cuenta.banco,
        nombre_cuenta: cuenta.nombre_cuenta,
        numero_cuenta: cuenta.numero_cuenta,
        tipo_cuenta: cuenta.tipo_cuenta,
        moneda: cuenta.moneda,
        activo: !cuenta.activo,
        integracion_tipo: cuenta.integracion_tipo,
        integracion_usuario: cuenta.integracion_usuario,
      }, token);
      cargarDatos();
    } catch {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo cambiar el estado' });
    }
  };

  const getBancoLabel = (val) => bancos.find(b => b.value === val)?.label || val;

  return (
    <div className="cb-page">
      <header className="cb-header">
        <button className="cb-back-btn" onClick={() => navigate('/conciliacion-bancaria')}>
          <FaArrowLeft /> Volver
        </button>
        <div>
          <h1 className="cb-page-title">Cuentas Bancarias</h1>
          <p className="cb-page-sub">Administra las cuentas y credenciales de integración por empresa</p>
        </div>
        <button className="cb-btn cb-btn--primary" onClick={() => { resetForm(); setShowForm(v => !v); }}>
          <FaPlus /> {showForm && !editingId ? 'Cancelar' : 'Nueva Cuenta'}
        </button>
      </header>

      {/* ── Formulario ── */}
      {showForm && (
        <div className="cb-form-container">
          <form onSubmit={handleSubmit} className="cb-form">
            <h3 className="cb-form-title">{editingId ? 'Editar Cuenta' : 'Nueva Cuenta Bancaria'}</h3>

            <div className="cb-form-grid">
              <div className="cb-form-group">
                <label>Banco *</label>
                <select name="banco" value={formData.banco} onChange={handleChange} disabled={!!editingId} required>
                  <option value="">Seleccionar banco...</option>
                  {bancos.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                </select>
              </div>

              <div className="cb-form-group">
                <label>Nombre de la cuenta</label>
                <input type="text" name="nombre_cuenta" value={formData.nombre_cuenta}
                  onChange={handleChange} placeholder="Ej: Cuenta Corriente Principal" />
              </div>

              <div className="cb-form-group">
                <label>Número de cuenta</label>
                <input type="text" name="numero_cuenta" value={formData.numero_cuenta}
                  onChange={handleChange} placeholder="Ej: 123456789" />
              </div>

              <div className="cb-form-group">
                <label>Tipo de cuenta</label>
                <select name="tipo_cuenta" value={formData.tipo_cuenta} onChange={handleChange}>
                  {TIPOS_CUENTA.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>

              <div className="cb-form-group">
                <label>Moneda</label>
                <select name="moneda" value={formData.moneda} onChange={handleChange}>
                  <option value="CLP">CLP - Peso chileno</option>
                  <option value="USD">USD - Dólar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="UF">UF</option>
                </select>
              </div>
            </div>

            {/* Integración directa */}
            <div className="cb-form-section">
              <h4 className="cb-form-section-title">Integración Directa (opcional)</h4>
              <p className="cb-form-hint">
                Configura credenciales solo si el banco tiene integración API disponible.
                Sin esto, puedes importar cartolas por Excel.
              </p>
              <div className="cb-form-grid">
                <div className="cb-form-group">
                  <label>Tipo de integración</label>
                  <select name="integracion_tipo" value={formData.integracion_tipo} onChange={handleChange}>
                    <option value="">Sin integración</option>
                    <option value="api">API REST</option>
                    <option value="scraping">Web Scraping</option>
                  </select>
                </div>

                <div className="cb-form-group">
                  <label>Usuario / RUT</label>
                  <input type="text" name="integracion_usuario" value={formData.integracion_usuario}
                    onChange={handleChange} placeholder="Usuario de acceso al banco" />
                </div>

                <div className="cb-form-group">
                  <label>Contraseña {editingId && <span className="cb-form-hint-inline">(vacío = mantener)</span>}</label>
                  <input type="password" name="integracion_password" value={formData.integracion_password}
                    onChange={handleChange} placeholder={editingId ? '••••••••' : 'Contraseña del banco'} />
                </div>

                <div className="cb-form-group">
                  <label>API Key {editingId && <span className="cb-form-hint-inline">(vacío = mantener)</span>}</label>
                  <input type="password" name="integracion_api_key" value={formData.integracion_api_key}
                    onChange={handleChange} placeholder={editingId ? '••••••••' : 'API Key del banco (si aplica)'} />
                </div>
              </div>
            </div>

            <div className="cb-form-actions">
              <button type="submit" className="cb-btn cb-btn--success">
                {editingId ? 'Actualizar' : 'Guardar'}
              </button>
              <button type="button" className="cb-btn cb-btn--secondary" onClick={resetForm}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Tabla de cuentas ── */}
      <div className="cb-table-container">
        {loading ? (
          <p className="cb-loading">Cargando cuentas...</p>
        ) : cuentas.length === 0 ? (
          <div className="cb-empty">
            <FaUniversity className="cb-empty-icon" />
            <p>No hay cuentas bancarias registradas.</p>
            <p>Agrega una cuenta para comenzar a importar cartolas.</p>
          </div>
        ) : (
          <table className="cb-table">
            <thead>
              <tr>
                <th>Banco</th>
                <th>Nombre / Número</th>
                <th>Tipo</th>
                <th>Moneda</th>
                <th>Integración</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {cuentas.map(c => (
                <tr key={c.id} className={!c.activo ? 'cb-row--inactive' : ''}>
                  <td><strong>{getBancoLabel(c.banco)}</strong></td>
                  <td>
                    <span>{c.nombre_cuenta || '-'}</span>
                    {c.numero_cuenta && <small className="cb-account-num"> · {c.numero_cuenta}</small>}
                  </td>
                  <td>{TIPOS_CUENTA.find(t => t.value === c.tipo_cuenta)?.label || c.tipo_cuenta}</td>
                  <td>{c.moneda}</td>
                  <td>
                    {c.integracion_tipo ? (
                      <div className="cb-integ-info">
                        <span className="cb-badge cb-badge--blue">{c.integracion_tipo}</span>
                        {c.tiene_api_key && <span className="cb-badge cb-badge--green"><FaKey /> API Key</span>}
                        {c.tiene_password && <span className="cb-badge cb-badge--gray">Pass</span>}
                      </div>
                    ) : (
                      <span className="cb-badge cb-badge--gray">Solo Excel</span>
                    )}
                  </td>
                  <td>
                    <span className={`cb-badge ${c.activo ? 'cb-badge--green' : 'cb-badge--red'}`}>
                      {c.activo ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td className="cb-actions">
                    <button
                      className={`cb-icon-btn ${c.activo ? 'cb-icon-btn--deactivate' : 'cb-icon-btn--activate'}`}
                      onClick={() => handleToggle(c)}
                      title={c.activo ? 'Desactivar' : 'Activar'}
                    >
                      {c.activo ? <FaToggleOn /> : <FaToggleOff />}
                    </button>
                    <button className="cb-icon-btn cb-icon-btn--edit" onClick={() => handleEdit(c)} title="Editar">
                      <FaEdit />
                    </button>
                    <button className="cb-icon-btn cb-icon-btn--delete"
                      onClick={() => handleDelete(c.id, getBancoLabel(c.banco))} title="Eliminar">
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default CuentasBancoPage;
